import React, {useEffect, useState, useRef, useImperativeHandle, forwardRef} from 'react';
import './Input.scss';

const isPromise = val =>
    val && typeof val.then === 'function' && typeof val.catch === 'function';

const Input = forwardRef(({label, options, onChange=() => {}, onSave, persistedValue, loadingMessage='Saving...', input=false, type='text', inputProps={}}, ref) => {

    const inputRef = useRef(null);

    const initialValue = persistedValue !== undefined
        ? (persistedValue ?? '')
        : (!input ? options[0].value : '');
    const [internalValue, internalValueSet]  = useState(initialValue);
    const [savedValue, savedValueSet] = useState(!input ? options[0].value : '');
    const [inputVisible, inputVisibleSet]  = useState(input);
    const [loading, setLoading] = useState(false);
    const [showOther, setShowOther] = useState(false);

    const internalTimer = useRef(null);
    const isInitialMount = useRef(true);
    const resolvedInputProps = inputProps && typeof inputProps === 'object' ? inputProps : {};

    useImperativeHandle(ref, () => ({
        setValue(val){
            internalValueSet(val);
            savedValueSet(val);
            if (options && Array.isArray(options)) {
                const optionValues = options.map(opt => opt.value);
                if (!optionValues.includes(val)) {
                    // If an 'other' option exists, select it instead of switching to input
                    if (optionValues.includes('other')) {
                        setShowOther(false);
                        inputVisibleSet(false);
                        internalValueSet('other');
                    } else {
                        // Fallback to previous behavior: switch to input mode
                        console.log("INPUT imperative hanlde", val);
                        setShowOther(true);
                        inputVisibleSet(true);
                        if (inputRef && inputRef.current) {
                            setTimeout(() => {
                                inputRef.current.focus();
                            }, 500);
                        }
                    }
                }
            }
            //console.log("2. Internal Value set", val);
        }
    }));


    const onChangeCallback = (newValue) => {
        let val = newValue
        if (val === '') {
            handleCancelOther();
        }
        let res = onChange(val);
        if (isPromise(res)) {
            setLoading(loadingMessage);
            res.then(() => {
                setLoading(false);
            }).catch(err => {
                console.error("Error in onChange promise:", err);
                setLoading(false);
            });
        }
    };

    const handleChange = (event) => {
        internalValueSet(event.target.value);

        // Uncomment this to let the user enter an input value when selecting "other" from the dropdown
        // if(event.target.value == 'other'){
        //     inputVisibleSet(true);
        //     internalValueSet('');
        //     setShowOther(true);
        //     return;
        // }

        if(inputVisible) {
            internalTimer.current && clearTimeout(internalTimer.current);
            if(!onSave){
                internalTimer.current = setTimeout(() => {
                    onChangeCallback(event.target.value);
                    internalTimer.current = null;
                }, 600);
            }
        }else{
            if(!onSave){
                onChangeCallback(event.target.value);
            }
            internalTimer.current && clearTimeout(internalTimer.current);
        }
    };

    const handleCancelOther = () => {
        // inputVisibleSet(false);
        // internalValueSet('');
        inputVisibleSet(false);
        internalValueSet('');
        setShowOther(false);
        handleChange({target: {value: null}});
        // onChangeCallback('');
    };

    const handleSave = () => {
        const valueToSave = internalValue;
        const result = onSave(valueToSave);

        if(isPromise(result)){
            setLoading(loadingMessage);
            result.then(() => {
                savedValueSet(valueToSave);
                setLoading(false);
            }).catch((error) => {
                console.error("Error in save promise:", error);
                setLoading(false);
            });
            return;
        }

        savedValueSet(valueToSave);
    };

    const comparisonValue = persistedValue !== undefined ? persistedValue : savedValue;
    const isDirty = typeof onSave === 'function' && String(internalValue ?? '') !== String(comparisonValue ?? '');

    useEffect(() => {
        if(inputVisible){
            // Only focus if this is not the initial mount (i.e., there was a transition from select to input)
            if (!isInitialMount.current) {
                setTimeout(() => {
                    inputRef.current.focus();
                }, 500);
            }
        }
        // Mark that initial mount is complete
        isInitialMount.current = false;
    }, [inputVisible]);

    return (
        <label className={`input-multiple ${isDirty ? 'has-pending-save' : ''}`}>
            {label && 
            <span>{label}</span>
            }
            {isDirty &&
                <button
                    type="button"
                    className="input-save"
                    onClick={handleSave}
                    disabled={loading}
                    title="Save"
                    aria-label={`Save ${label || 'field'}`}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M5 3h12l3 3v15H4V3h1Zm2 2v5h9V5H7Zm0 8v6h10v-6H7Zm2 2h6v2H9v-2Z" />
                    </svg>
                </button>
            }
            {!input &&!inputVisible && options && options.length > 0 &&
            <select disabled={loading} placeholder="Pick one" value={!internalValue?'':internalValue} style={internalValue?{}:{color: '#65666F'}} onChange={handleChange}>
                <option value="" disabled selected >Pick an option</option>
                {options.map( (option, index) =>
                <option key={`input-${label}-${index}`} value={option.value}>{option.label}</option>
                )}
            </select>
            }
            {(input || inputVisible) &&
                <input {...resolvedInputProps} type={type} disabled={loading} value={!internalValue?'':internalValue} onChange={handleChange} ref={inputRef} />
            }
            {!input && showOther && 
                <span 
                    className='glyphicon glyphicon-cross' 
                    style={{ cursor: 'pointer', position: 'absolute', right: '4px', minWidth: 'unset', fontWeight: 400, fontSize: '8px', color: '#65666f' }}
                    onClick={handleCancelOther} ></span>
            }
            {loading && <span className="loading">{loadingMessage}</span>}
        </label>
    );
});

export default Input;
